import {
  Home,
  CloudRain,
  Grid3x3,
  Layers,
  Rows3,
  Droplets,
  AppWindow,
  Fence,
  ArrowRight,
  Snowflake,
  Lightbulb,
  Building2,
  Sparkles,
} from "lucide-react";
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Reveal } from "./reveal";
import { openQuoteModal } from "./quote-modal";

const services = [
  {
    icon: Home,
    name: "House Washing",
    desc: "Soft-wash siding, brick, and stucco. Lifts mold, mildew, and grime without damage.",
    href: "/house-washing",
  },
  {
    icon: CloudRain,
    name: "Roof Washing",
    desc: "Low-pressure soft wash that clears black streaks and moss. Safe for your shingles.",
    href: "/roof-washing",
  },
  {
    icon: Grid3x3,
    name: "Paver Cleaning & Sealing",
    desc: "Deep-clean pavers, re-sand the joints, and seal to lock out weeds and stains.",
    href: "/paver-cleaning-sealing",
  },
  {
    icon: AppWindow,
    name: "Window Cleaning",
    desc: "Streak-free windows inside and out. Let the light back in.",
    href: "/window-cleaning",
  },
  {
    icon: Layers,
    name: "Concrete & Driveway",
    desc: "Blast dirt and oil off driveways, sidewalks, and walkways. Optional sealing.",
    href: "/concrete-cleaning-sealing",
  },
  {
    icon: Rows3,
    name: "Deck & Patio Cleaning",
    desc: "Strip dirt and graying from wood and composite. Restore a clean, even finish.",
    href: "/deck-patio-cleaning",
  },
  {
    icon: Droplets,
    name: "Gutter Cleaning",
    desc: "Clear clogged gutters, flush every downspout, and inspect the hardware.",
    href: "/gutter-cleaning",
  },
  {
    icon: Fence,
    name: "Fence Cleaning",
    desc: "Wash away dirt, algae, and stains from wood, vinyl, and chain-link.",
    href: "/fence-cleaning",
  },
  {
    icon: Snowflake,
    name: "Holiday Lighting",
    desc: "Design, install, take-down, and storage of professional Christmas lighting.",
    href: "/holiday-lighting",
  },
  {
    icon: Lightbulb,
    name: "Permanent Outdoor Lighting",
    desc: "Year-round programmable rooflines and accent lighting — holidays to game day.",
    href: "/permanent-lighting",
  },
  {
    icon: Building2,
    name: "Commercial Washing",
    desc: "Storefronts, buildings, brick, and dumpster pads. Kept clean on a schedule.",
    href: "/commercial-pressure-washing",
  },
  {
    icon: Sparkles,
    name: "Roof Rejuvenation",
    desc: "Soy-based asphalt shingle treatment designed to extend roof life. 6-year warranty.",
    href: "/roof-rejuvenation",
  },
];

export function HomeServices() {
  const [showAll, setShowAll] = useState(false);
  return (
    <section id="services" className="bg-white py-16 md:py-20 scroll-mt-24">
      <div className="mx-auto max-w-[1200px] px-4">
        <div className="text-center mb-10 md:mb-12">
          <p className="text-[#C8102E] font-semibold uppercase tracking-[0.2em] text-sm mb-3">
            What We Do
          </p>
          <h2
            className="text-[#0B3D7A] text-3xl md:text-5xl uppercase leading-tight"
            style={{ fontFamily: "Oswald, sans-serif", fontWeight: 700 }}
          >
            Pressure Washing Services for the Capital Region
          </h2>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          style={{ perspective: "1000px" }}
        >
          {services.map(({ icon: Icon, name, desc, href }, i) => (
            <Reveal
              as="a"
              key={name}
              delayMs={i * 90}
              variant="flap"
              href={href}
              className={
                "card-pop group h-full flex-col bg-white border border-gray-200 rounded-lg p-6 text-center sm:text-left shadow-sm sm:flex " +
                (i >= 4 && !showAll ? "hidden" : "flex")
              }
            >
              <div className="flex justify-center sm:justify-start mb-4">
                <Icon className="icon-wiggle text-[#C8102E]" size={40} strokeWidth={1.75} />
              </div>
              <h3
                className="text-[#0B3D7A] text-lg mb-2 uppercase"
                style={{ fontFamily: "Oswald, sans-serif", fontWeight: 700 }}
              >
                {name}
              </h3>
              <p className="text-[#1A1A1A] text-sm leading-relaxed mb-4 flex-1">
                {desc}
              </p>
              <span className="inline-flex items-center gap-1 text-[#C8102E] font-bold text-sm uppercase tracking-wide group-hover:underline mt-auto">
                Learn More<span className="sr-only"> about {name}</span>{" "}
                <ArrowRight size={14} />
              </span>
            </Reveal>
          ))}
        </div>

        {!showAll && (
          <div className="mt-8 text-center sm:hidden">
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="inline-flex items-center justify-center gap-2 min-h-[44px] px-6 rounded-md border-2 border-[#0B3D7A] text-[#0B3D7A] font-bold uppercase tracking-wide text-sm"
            >
              See All Services
              <ChevronDown size={16} />
            </button>
          </div>
        )}

        <div className="text-center mt-12">
          <p className="text-[#1A1A1A] text-lg mb-5">
            Not sure what you need? We'll tell you straight, free.
          </p>
          <button
            type="button"
            onClick={() => openQuoteModal()}
            className="btn-pop btn-shimmer btn-jiggle inline-block bg-[#C8102E] hover:bg-[#a50d25] text-white font-bold uppercase tracking-wide px-8 py-4 rounded-md transition-colors min-h-[44px]"
          >
            Get Free Quote
          </button>
        </div>
      </div>
    </section>
  );
}
